/**
 * usePageLiveStream — the live-streaming HTML ingestion sub-system of PageRenderer.
 *
 * Owns the rebuildOverlay / liveHtml / streamFinished state and the
 * rAF-coalesced scheduleLiveHtml setter. Wires the `xavior:html-config-update`
 * listener (overlay/reload/section-frame protocol), the object-id re-seed,
 * the canonical-html catch-up, and the lazy single-GET fetch when the list
 * payload truncated the html.
 *
 * Ref-coupling (STRICTLY one-way): the orchestrator OWNS baselineHtmlRef /
 * renderedHtmlRef / iframeReadyRef / pendingSectionFramesRef + the
 * drainSectionFrameQueue callback + setForceFullReload, and threads them in.
 * This hook NEVER re-creates those refs and NEVER imports PageRenderer.
 *
 * The 3-family live-seed keys [liveKey, legacyModuleId, derivedRawModuleId]
 * and the pg_ prefix handling are derived in the orchestrator and passed in
 * verbatim so the mount-time seed ordering is identical.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { api } from '@/lib/api';
import { getLiveHtml } from '@/lib/html-live-state';
import { rawPageId } from '@/lib/page-id';

interface UsePageLiveStreamArgs {
  liveKey: string;
  legacyModuleId: string | undefined;
  liveSeedKeys: (string | undefined)[];
  matchesLiveId: (incoming: unknown) => boolean;
  html: string | undefined;
  objectId: string | undefined;
  token: string | null | undefined;
  baselineHtmlRef: React.MutableRefObject<string | null>;
  renderedHtmlRef: React.MutableRefObject<string | null>;
  iframeReadyRef: React.MutableRefObject<boolean>;
  pendingSectionFramesRef: React.MutableRefObject<import('../types').PendingSectionFrame[]>;
  drainSectionFrameQueue: (frames: import('../types').PendingSectionFrame[]) => void;
  setForceFullReload: React.Dispatch<React.SetStateAction<number>>;
}

export function usePageLiveStream({
  liveKey,
  legacyModuleId,
  liveSeedKeys,
  matchesLiveId,
  html,
  objectId,
  token,
  baselineHtmlRef,
  renderedHtmlRef,
  iframeReadyRef,
  pendingSectionFramesRef,
  drainSectionFrameQueue,
  setForceFullReload,
}: UsePageLiveStreamArgs) {
  // Rebuild overlay — visible during an html.init-for-edit stream so the
  // user sees a blurred placeholder instead of the iframe flashing through
  // successive intermediate HTML states mid-stream.
  const [rebuildOverlay, setRebuildOverlay] = useState(false);

  const [liveHtml, setLiveHtml] = useState<string | null>(() => {
    for (const key of liveSeedKeys) {
      if (!key) continue;
      const entry = getLiveHtml(key);
      if (entry?.html) return entry.html;
    }
    return null;
  });
  const [streamFinished, setStreamFinished] = useState(() => {
    for (const key of liveSeedKeys) {
      if (!key) continue;
      const entry = getLiveHtml(key);
      if (entry?.final) return true;
    }
    return false;
  });

  // rAF-coalesced live-html setter: upstream dispatches
  // `xavior:html-config-update` roughly every ~300ms with the full cumulative
  // HTML string. Hopping straight into React state on every event re-renders
  // the tree and reassigns the iframe's `srcDoc` prop — which forces a full
  // document reparse + repaint every time. By coalescing through rAF we
  // collapse bursts into a single state commit per paint, without dropping
  // intermediate content (we always keep the LATEST cumulative string).
  const pendingHtmlRef = useRef<string | null>(null);
  const htmlRafRef = useRef<number | null>(null);
  const scheduleLiveHtml = useCallback((html: string) => {
    pendingHtmlRef.current = html;
    if (htmlRafRef.current != null) return;
    htmlRafRef.current = requestAnimationFrame(() => {
      htmlRafRef.current = null;
      if (pendingHtmlRef.current != null) {
        setLiveHtml(pendingHtmlRef.current);
        pendingHtmlRef.current = null;
      }
    });
  }, []);

  useEffect(() => () => {
    if (htmlRafRef.current != null) {
      cancelAnimationFrame(htmlRafRef.current);
      htmlRafRef.current = null;
    }
  }, []);

  useEffect(() => {
    function onHtmlUpdate(e: Event) {
      const detail = (e as CustomEvent).detail as {
        moduleId: string;
        html?: string;
        final?: boolean;
        visual?: boolean;
        mode?: 'initial' | 'reload';
      };
      // Diagnostic: log every config-update event we receive + whether it
      // matches our keys. Helps catch moduleId mismatches that silently
      // drop chunks.
      console.log('[PageRenderer] xavior:html-config-update', {
        eventModuleId: detail.moduleId,
        ourLiveKey: liveKey,
        ourLegacyId: legacyModuleId,
        match: matchesLiveId(detail.moduleId),
        htmlBytes: detail.html?.length ?? 0,
        final: detail.final,
        visual: detail.visual,
        mode: detail.mode,
      });
      if (!matchesLiveId(detail.moduleId)) return;

      // Overlay protocol for html.init-for-edit ONLY: when the agent runs a
      // full html.init on an already-rendered page, intermediate chunks would
      // otherwise cause the iframe to re-parse partial HTML and flash. We
      // show an overlay over the existing content until the final html is
      // ready.
      //
      // NEW BUILDS must NOT trigger this overlay — if there's no existing
      // content for this page (first-ever build streaming in), the overlay
      // would just hide the progressive stream and the user stares at a
      // blank screen for the entire build. "Existing content" = either the
      // object already had saved html (editing/reopening a built page) OR
      // the render pipeline has established a baseline from a prior stream.
      const hasExistingContent =
        !!baselineHtmlRef.current || !!renderedHtmlRef.current || !!html;
      if (detail.mode === 'initial' && !detail.final && hasExistingContent) {
        setRebuildOverlay(true);
        return; // Old iframe stays visible under overlay; new chunks queued.
      }
      if (detail.mode === 'initial' && detail.final) {
        setRebuildOverlay(false);
        // Force a full srcDoc reassign for the final html so the iframe always
        // shows the completed page in one clean step. Reset the baseline so the
        // effectiveHtml effect takes the full-reload branch (not the patch path).
        baselineHtmlRef.current = null;
        renderedHtmlRef.current = null;
        iframeReadyRef.current = false;
        if (detail.html) scheduleLiveHtml(detail.html);
        setStreamFinished(true);
        return;
      }
      // mode:'initial' but no existing baseline → treat as a new build: fall
      // through so the default `scheduleLiveHtml(detail.html)` below renders
      // chunks progressively.

      // SectionFrame protocol: html.complete on a streamed build sends
      // `final:true, visual:false` — sections are already visually in
      // place via xavior:html-section-update, so we must NOT reload the
      // iframe. But we DO still need to drain any pending section frames
      // that were queued while the iframe was parsing, otherwise the user
      // sees only section 1 when html.complete arrives mid-load.
      if (detail.final && detail.visual === false) {
        setStreamFinished(true);
        if (iframeReadyRef.current) {
          const frames = pendingSectionFramesRef.current;
          pendingSectionFramesRef.current = [];
          drainSectionFrameQueue(frames);
        }
        return;
      }
      // New shell after the build already started — force a covered full
      // reload. This handles "restart, make it darker" style commands: the
      // agent emits a fresh shell + new sections, and we must not
      // Frankenstein-append them into the old DOM.
      if (detail.mode === 'reload') {
        baselineHtmlRef.current = null;
        renderedHtmlRef.current = null;
        iframeReadyRef.current = false;
        if (detail.html) scheduleLiveHtml(detail.html);
        setForceFullReload(n => n + 1);
        return;
      }
      if (detail.html) scheduleLiveHtml(detail.html);
      if (detail.final) setStreamFinished(true);
    }
    window.addEventListener('xavior:html-config-update', onHtmlUpdate as EventListener);
    return () => window.removeEventListener('xavior:html-config-update', onHtmlUpdate as EventListener);
    // `html` included so the hasExistingContent gate reads the latest prop
    // value when the object's saved html populates after a lazy single-GET.
  }, [liveKey, legacyModuleId, matchesLiveId, scheduleLiveHtml, html]);

  // Re-seed when the object id changes — same family-key fallback as the
  // mount-time seed (the live-state map keys by the RAW module id).
  useEffect(() => {
    let seeded: { html?: string; final?: boolean } | undefined;
    for (const key of [liveKey, legacyModuleId, liveKey?.startsWith('pg_') ? rawPageId(liveKey) : undefined]) {
      if (!key) continue;
      const entry = getLiveHtml(key);
      if (entry?.html || entry?.final) { seeded = entry; break; }
    }
    setLiveHtml(seeded?.html || null);
    setStreamFinished(seeded?.final ?? false);
    // Switching objects invalidates the patch baseline — force the next
    // render to use srcDoc. Nested refs defined later in the component
    // (`baselineHtmlRef`, `iframeReadyRef`) are reset in the same render
    // path via the `effectiveHtml` effect.
    // Also tear down any rebuild overlay that was visible for the previous
    // page — navigating away should never leave a stuck overlay.
    setRebuildOverlay(false);
  }, [liveKey, legacyModuleId]);

  // Clear live state once the canonical html catches up
  useEffect(() => {
    if (html && liveHtml) setLiveHtml(null);
  }, [html]);

  // Fetch full page content when the list endpoint stripped attributes.html
  useEffect(() => {
    if (html || liveHtml) return;
    if (!objectId || !token) return;
    let cancelled = false;
    (async () => {
      try {
        const full = await api.objects.getObject(token, 'page', objectId);
        // `getObject` returns { object, etagKey }. The API mapper (mapObject) spreads
        // the entity's attributes at the top level, so html lives on the object root
        // (NOT under .attributes). Fall back to .attributes.html defensively in case
        // the wire shape ever nests it.
        const obj = full?.object as
          | { html?: string; attributes?: { html?: string } }
          | undefined;
        const fetchedHtml = obj?.html ?? obj?.attributes?.html;
        if (!cancelled && typeof fetchedHtml === 'string' && fetchedHtml.length > 0) {
          setLiveHtml(fetchedHtml);
        }
      } catch (err) {
        console.error('[PageRenderer] Failed to fetch page content:', err);
      }
    })();
    return () => { cancelled = true; };
  }, [objectId, html, liveHtml, token]);

  return { liveHtml, setLiveHtml, streamFinished, rebuildOverlay, scheduleLiveHtml };
}
